// src/i18n/DatePickersProvider.jsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { daDK, deDE, enUS, nbNO, svSE } from '@mui/x-date-pickers/locales';
import { getDateLocale } from './LocalizedDateDistance';

// Picker texts (button labels, field placeholders) per UI language
const PICKER_LOCALES = { da: daDK, nb: nbNO, sv: svSE, de: deDE, en: enUS };

// The locale packs leave the year placeholder as "YYYY"; write it the way
// the language does (ÅÅÅÅ in Danish, like the browser's own date fields)
const YEAR_LETTER = { da: 'Å', nb: 'Å', sv: 'Å', de: 'J' };

/**
 * App-wide date picker localisation: field format, weekday names, first day
 * of week and picker texts follow the UI language. Pages must not add their
 * own <LocalizationProvider> — one without `adapterLocale` resets everything
 * below it to US format (MM/DD/YYYY).
 */
export default function DatePickersProvider({ children }) {
  const { i18n } = useTranslation();
  const language = (i18n.language || 'en').split('-')[0];

  const localeText = useMemo(() => {
    const pack = (PICKER_LOCALES[language] || enUS).components.MuiLocalizationProvider.defaultProps.localeText;
    const yearLetter = YEAR_LETTER[language];
    return yearLetter
      ? { ...pack, fieldYearPlaceholder: (params) => yearLetter.repeat(params.digitAmount) }
      : pack;
  }, [language]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={getDateLocale(language)}
      localeText={localeText}
    >
      {children}
    </LocalizationProvider>
  );
}
